/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useState, useRef} from 'react';
import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Radio,
	Select,
	Space,
	TimePicker,
} from "antd";
import dayjs from "dayjs";
import styles from './MakeBaechaForm.module.scss';
import {emptySearchGroup, GroupSearchSelect} from "../../../../components/common/selector/GroupSearchSelect";
import {
	carDetailOptions,
	generalCarOptions,
	guljeolCarOptions,
	Option,
	workDurationOptions,
	workEtcOptions
} from "./MakeBaechaForm.interface";
import groupAPI from "../../../../api/groupAPI";
import memberAPI from "../../../../api/memberAPI";
import {BaechaType} from "../../../../types/Type";
import BaljuDTO from "../../../BaechaList/MakeBaecha/dto/BaljuDTO";
import WorkLocInput from "./WorkLocInput/WorkLocInput";

interface MakeBaechaFormProps {
	onSubmit: (baljuDTO: BaljuDTO) => any;
	onCancel: () => any;
}

const MakeBaechaForm = ({ onSubmit, onCancel }: MakeBaechaFormProps) => {
	const [form] = Form.useForm();
	const [selectedGroup, setSelectedGroup] = useState(emptySearchGroup);
	const [memberOptions, setMemberOptions] = useState<Option[]>([]);
	const [loading, setLoading] = useState(false);
	const [isGuljeol, setIsGuljeol] = useState(false);
	const [carTypeOptions, setCarTypeOptions] = useState<Option[]>(generalCarOptions);
	const [groupRule, setGroupRule] = useState<any>({});
	const [memberCarList, setMemberCarList] = useState<any[]>([]);

	const latestGroupIdRef = useRef<string>("");

	const onClickGroupName = async (data: any) => {
		setSelectedGroup(data);

		setMemberOptions([]);
		form.resetFields(['member', 'baljuMemberCarId']);

		latestGroupIdRef.current = data.value;

		setLoading(true);
		try {
			const list = await groupAPI.fetchGroupMemberList(data.value);
			const rules = await groupAPI.getGroupConfigs(data.value);
			setGroupRule(rules.config);
			if (latestGroupIdRef.current !== data.value) return;

			const options: Option[] = list.map((item: any) => ({
				value: item.id,
				label: item.nickname,
			}));
			setMemberOptions(options);
		} finally {
			if (latestGroupIdRef.current === data.value) setLoading(false);
		}
	};

	const onSelectBaljuMember = async (memberId: string) => {
		form.resetFields(['baljuMemberCarId']);
		const list = await memberAPI.getMemberCarList(memberId);
		const newMemberCarList = list.filter((item: any) => item.memberId === memberId)
			.map((item: any) => ({
				value: item.memberCarId,
				label: `${item.carType} ${item.number}`,
			}));
		setMemberCarList(newMemberCarList);
		form.setFieldValue('baljuMemberCarId', newMemberCarList[0]?.value);
	};

	const getBaechaType = (isUrgent: boolean | undefined, designatedMemberId: string | undefined): BaechaType => {
		if (isUrgent) return 'urgent';
		if (designatedMemberId) return 'designated';
		return 'normal';
	};

	const getWorkHour = (values: any) => {
		const durationSelect = values.durationSelect;

		if (durationSelect === '1h') return 1;
		if (durationSelect === '2h') return 2;
		if (durationSelect === '4h') return 4;
		return undefined;
	};

	const getWorkDay = (values: any) => {
		const durationSelect = values.durationSelect;
		const durationDays = values.durationDays;

		if (durationSelect === '1d') return 1;
		if (durationSelect === '2d') return 2;
		if (durationSelect === '3d') return 3;
		if (durationSelect === 'm') return 0;
		return durationDays;
	};

	const getWorkArriveDate = (values: any) => {
		const date = values.date;
		const time = values.time;

		return dayjs(date)
			.tz('Asia/Seoul')
			.set('hour', time.hour())
			.set('minute', time.minute())
			.toDate();
	};

	const handleFinish = async (values: any) => {
		const baljuDTO = new BaljuDTO({
			baechaType: getBaechaType(values.isUrgent, values.designatedMemberId),
			baljuGroupId: values.group.value,
			baljuMemberCarId: values.baljuMemberCarId,
			baljuMemberId: values.baljuMemberId,
			baljuingGroupId: values.group.value,
			carDetail: values.carDetail?.join(', '),
			carType: values.carType,
			designatedMemberId: values.designatedMemberId,
			isLink: values.isLink,
			isNoFee: values.isNoFee,
			isSpecificCarOnly: values.isSpecificCarOnly,
			isWorkMonth: values.durationSelect === 'm',
			price: values.price * 10_000,
			priceReceiveMethod: values.priceReceiveMethod,
			workArriveDate: getWorkArriveDate(values),
			workContent: values.workContent,
			workEtc: values.workEtc?.join(', '),
			workDay: getWorkDay(values),
			workHour: getWorkHour(values),
			workLoc: values.workLoc,
			privatePhone: values.privatePhone,
			privateAddress: values.privateAddress,
			privateNote: values.privateNote,
			latitude: values.latitude,
			longitude: values.longitude,
			region: values.region,
		});
		onSubmit(baljuDTO);
	};

	return (
		<div className={styles.wrap}>
			<Form
				form={form}
				onFinish={handleFinish}
				style={{ paddingTop: '20px' }}
				clearOnDestroy
			>
				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					{/* 배차방 */}
					<Form.Item name="group" label="배차방" rules={[{ required: true, message: '배차방을 선택하세요' }]} style={{ width: '200px'}}>
						<GroupSearchSelect
							selectedGroup={selectedGroup}
							onChange={onClickGroupName}
							placeholder="배차방 검색"
						/>
					</Form.Item>

					{/* 발주 멤버 */}
					<Form.Item name="baljuMemberId" label="발주 멤버" rules={[{ required: true, message: '발주 멤버를 선택하세요' }]} style={{ width: '250px'}}>
						<Select
							placeholder="발주 멤버 선택"
							options={memberOptions}
							loading={loading}
							allowClear
							showSearch
							optionFilterProp="label"
							onChange={onSelectBaljuMember}
						/>
					</Form.Item>
				</Space>


				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item label="차량 유형">
						<Radio.Group
							value={isGuljeol ? 'guljeol' : 'general'}
							onChange={(e) => {
								const selected = e.target.value;
								const isGul = selected === 'guljeol';
								setIsGuljeol(isGul);

								setCarTypeOptions(isGul ? guljeolCarOptions : generalCarOptions);

								form.setFieldValue('carType', undefined);
								form.setFieldValue('carDetail', []);
								form.setFieldValue('isSpecificCarOnly', false);
							}}
						>
							<Radio value="general">일반</Radio>
							<Radio value="guljeol">굴절</Radio>
						</Radio.Group>
					</Form.Item>

					{/* 차량	*/}
					<Form.Item name="carType" label="차량" rules={[{ required: true, message: '차량을 선택하세요' }]}>
						<Select
							placeholder="차량 선택"
							style={{ width: 200 }}
							options={carTypeOptions}
						/>
					</Form.Item>
				</Space>

				{!isGuljeol && (
					<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
						<Form.Item name="isSpecificCarOnly" valuePropName="checked">
							<Checkbox
								onChange={(e) => {
									const checked = e.target.checked;
									setCarTypeOptions((prev) =>
										prev.map((item) => {
											if (!checked) {
												return { ...item, label: item.label.replace(/만$/, '') };
											}

											if (!item.label.endsWith('만')) {
												return { ...item, label: `${item.label}만` };
											}

											return item;
										})
									);
								}}
							>
								차종 제한
							</Checkbox>
						</Form.Item>

						<Form.Item name="carDetail" label="추가 옵션" style={{ width: '350px' }}>
							<Select
								mode="tags"
								style={{ width: "100%" }}
								placeholder="옵션 선택 또는 직접 입력"
								tokenSeparators={[","]}
								options={carDetailOptions}
							/>
						</Form.Item>
					</Space>
				)}

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item name="isUrgent" valuePropName="checked" style={{ width: '90px' }}>
						<Checkbox
							onChange={(e) => {
								if (e.target.checked) {
									form.setFieldsValue({ date: dayjs() });
								}
							}}
						>
							긴급 배차
						</Checkbox>
					</Form.Item>
					<Form.Item name="date" label="도착일" rules={[{ required: true, message: '도착일을 선택하세요' }]}>
						<DatePicker
							disabled={form.getFieldValue('isUrgent')}
							showToday={false}
							renderExtraFooter={() => (
								<Space style={{ width: "100%", display: 'flex', justifyContent: 'space-evenly' }}>
									<Button type="link" size="small" onClick={() => form.setFieldsValue({ date: dayjs() })}>
										오늘
									</Button>
									<Button type="link" size="small" onClick={() => form.setFieldsValue({ date: dayjs().add(1, "day") })}>
										내일
									</Button>
									<Button type="link" size="small" onClick={() => form.setFieldsValue({ date: dayjs().add(2, "day") })}>
										모레
									</Button>
								</Space>
							)}
						/>
					</Form.Item>
					<Form.Item name="time" label="도착시간" rules={[{ required: true, message: '도착시간을 선택하세요' }]}>
						<TimePicker format="HH:mm" />
					</Form.Item>
				</Space>

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item
						name="durationSelect"
						label="작업일정"
						rules={[{ validator: (_, value) => (value || form.getFieldValue('durationDays'))
								? Promise.resolve()
								: Promise.reject(new Error('작업일정을 선택하거나 직접 입력하세요')),
						}]}
					>
						<Select
							style={{ width: 160 }}
							placeholder="작업일정 선택"
							options={workDurationOptions}
							onChange={() => {
								form.resetFields(['durationDays']);
							}}
						/>
					</Form.Item>

					<Form.Item
						name="durationDays"
						rules={[{ validator: (_, value) => (value || form.getFieldValue('durationSelect'))
								? Promise.resolve()
								: Promise.reject(new Error('작업일정을 선택하거나 직접 입력하세요')),
						}]}
					>
						<InputNumber
							min={1}
							placeholder="직접 입력"
							onChange={() => {
								form.resetFields(['durationSelect']);
							}}
							addonAfter="일"
						/>
					</Form.Item>
				</Space>

				<Form.Item name="price" label="총액" rules={[{ required: true, message: '총액을 입력하세요' }]}>
					<InputNumber addonAfter="만 원" style={{ width: '100%' }} />
				</Form.Item>

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
					<Form.Item name="isNoFee" valuePropName="checked">
						<Checkbox>무콜</Checkbox>
					</Form.Item>
					<Form.Item
						name="priceReceiveMethod"
						label="결제"
						rules={[{ required: true, message: '결제 방법을 선택하세요' }]}
						initialValue="direct_ad"
					>
						<Radio.Group>
							<Radio value="direct_ad">직수</Radio>
							<Radio value="direct_noad">현수</Radio>
							<Radio value="sign">싸인</Radio>
						</Radio.Group>
					</Form.Item>
				</Space>

				<WorkLocInput form={form} />

				<Form.Item name="workContent" label="작업 내용">
					<Input />
				</Form.Item>

				<Form.Item name="workEtc" label="전달 및 특이사항">
					<Select
						mode="tags"
						style={{ width: "100%" }}
						placeholder="옵션 선택 또는 직접 입력"
						tokenSeparators={[","]}
						options={workEtcOptions}
					/>
				</Form.Item>

				<Form.Item
					name="baljuMemberCarId"
					label="점수 적립할 차량"
					hidden={!groupRule.enable_point_to_car}
				>
					<Select
						placeholder="점수 적립할 차량 선택"
						options={memberCarList}
						loading={loading}
						allowClear
						showSearch
						optionFilterProp="label"
					/>
				</Form.Item>

				<Form.Item name="designatedMemberId" label="지정 배차">
					<Select
						placeholder="지정배차 멤버 선택"
						options={memberOptions}
						loading={loading}
						allowClear
						showSearch
						optionFilterProp="label"
					/>
				</Form.Item>

				<Form.Item name="privateAddress" label="현장 주소(수주자에게만 공개)">
					<Input />
				</Form.Item>
				<Form.Item name="privatePhone" label="현장 연락처(수주자에게만 공개)">
					<Input />
				</Form.Item>
				<Form.Item name="privateNote" label="기타(수주자에게만 공개)">
					<Input />
				</Form.Item>

				<Form.Item name="isLink" valuePropName="checked">
					<Checkbox>연합배차 여부</Checkbox>
				</Form.Item>

				<Space style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
					<Form.Item>
						<Button
							onClick={() => {
								form.resetFields();
								onCancel();
							}}
						>
							취소
						</Button>
						<Button type="primary" htmlType="submit"  style={{ marginLeft: '15px' }}>
							저장
						</Button>
					</Form.Item>
				</Space>
			</Form>
		</div>
	);
};

export default MakeBaechaForm;
